import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type { PatchOp } from "@/lib/blocks";
import { api, isApiError } from "./client";
import type { Note, NoteSummary, Space, Thread, User } from "./types";

export const keys = {
  me: ["me"] as const,
  spaces: ["spaces"] as const,
  space: (id: string) => ["space", id] as const,
  spaceNotes: (id: string, q?: string) => ["space", id, "notes", q ?? ""] as const,
  spaceTree: (id: string) => ["space", id, "tree"] as const,
  note: (id: string) => ["note", id] as const,
  noteVersions: (id: string) => ["note", id, "versions"] as const,
  noteShares: (id: string) => ["note", id, "shares"] as const,
  threads: (space?: string) => ["threads", space ?? ""] as const,
  thread: (id: string) => ["thread", id] as const,
  recent: (space?: string) => ["recent", space ?? ""] as const,
  search: (q: string, space?: string) => ["search", q, space ?? ""] as const,
  tokens: ["tokens"] as const,
  invites: ["invites"] as const,
  invite: (token: string) => ["invite", token] as const,
  users: ["users"] as const,
  signInProviders: ["auth", "providers"] as const,
  identities: ["auth", "identities"] as const,
  share: (token: string) => ["share", token] as const,
  oauthRequest: (id: string) => ["oauth-request", id] as const,
};

/* ---------------- auth ---------------- */

export function useMe() {
  return useQuery<User | null>({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api.auth.me();
      } catch (e) {
        if (isApiError(e, 401)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useSignInProviders() {
  return useQuery({ queryKey: keys.signInProviders, queryFn: api.auth.providers, staleTime: 10 * 60_000 });
}

export function useIdentities() {
  return useQuery({ queryKey: keys.identities, queryFn: api.auth.identities });
}

export function useUnlinkIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.auth.unlink(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.identities }),
  });
}

/** `/api/auth/<provider>/start` is a full-page redirect, not an XHR: the provider sets cookies on the way back. */
export function signInUrl(provider: string, opts: { returnTo?: string; link?: boolean; invite?: string } = {}): string {
  const p = new URLSearchParams();
  if (opts.returnTo && opts.returnTo !== "/") p.set("returnTo", opts.returnTo);
  if (opts.link) p.set("link", "1");
  if (opts.invite) p.set("invite", opts.invite);
  const q = p.toString();
  return `/api/auth/${encodeURIComponent(provider)}/start${q ? `?${q}` : ""}`;
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSettled: () => {
      qc.clear();
      window.location.assign("/");
    },
  });
}

/* ---------------- spaces ---------------- */

export function useSpaces() {
  return useQuery<Space[]>({ queryKey: keys.spaces, queryFn: api.spaces.list, staleTime: 60_000 });
}

export function useSpace(id: string | undefined) {
  return useQuery<Space>({
    queryKey: keys.space(id ?? ""),
    queryFn: () => api.spaces.get(id!),
    enabled: !!id,
  });
}

export function useSpaceNotes(id: string | undefined, q?: string) {
  return useQuery<NoteSummary[]>({
    queryKey: keys.spaceNotes(id ?? "", q),
    queryFn: () => api.spaces.notes(id!, { q: q || undefined, limit: 200 }),
    enabled: !!id,
    placeholderData: (prev) => prev,
  });
}

export function useSpaceTree(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.spaceTree(id ?? ""),
    queryFn: () => api.spaces.tree(id!),
    enabled: !!id && enabled,
    staleTime: 30_000,
  });
}

export function useCreateSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.spaces.create(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.spaces }),
  });
}

function invalidateNoteLists(qc: ReturnType<typeof useQueryClient>, spaceId?: string) {
  if (spaceId) qc.invalidateQueries({ queryKey: keys.space(spaceId) });
  qc.invalidateQueries({ queryKey: ["recent"] });
  qc.invalidateQueries({ queryKey: ["search"] });
  qc.invalidateQueries({ queryKey: ["threads"] });
}

export function useCreateNote(spaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string; markdown: string; parentId?: string | null }) =>
      api.spaces.createNote(spaceId!, body),
    onSuccess: (note) => {
      qc.setQueryData(keys.note(note.id), note);
      invalidateNoteLists(qc, spaceId);
      if (note.parentId) qc.invalidateQueries({ queryKey: keys.note(note.parentId) });
    },
  });
}

/* ---------------- notes ---------------- */

export function useNote(id: string | undefined, opts?: Partial<UseQueryOptions<Note>>) {
  return useQuery<Note>({
    queryKey: keys.note(id ?? ""),
    queryFn: () => api.notes.get(id!),
    enabled: !!id,
    ...opts,
  });
}

export function usePatchNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, ops }: { version: number; ops: PatchOp[] }) =>
      api.notes.patch(id, version, ops),
    onSuccess: (note) => {
      qc.setQueryData(keys.note(id), note);
      invalidateNoteLists(qc, note.spaceId);
    },
  });
}

export function usePutNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { version: number; markdown: string; title?: string }) =>
      api.notes.put(id, body),
    onSuccess: (note) => {
      qc.setQueryData(keys.note(id), note);
      invalidateNoteLists(qc, note.spaceId);
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; spaceId?: string; parentId?: string | null }) =>
      api.notes.remove(id),
    onSuccess: (_d, vars) => {
      qc.removeQueries({ queryKey: keys.note(vars.id) });
      invalidateNoteLists(qc, vars.spaceId);
      if (vars.parentId) qc.invalidateQueries({ queryKey: keys.note(vars.parentId) });
    },
  });
}

export function useMoveNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { parentId: string | null; position?: number; spaceId?: string }) => api.notes.move(id, body),
    onSuccess: (result) => {
      qc.setQueryData(keys.note(id), result.note);
      // Both spaces' trees and lists change on a cross-space move; one space otherwise.
      invalidateNoteLists(qc, result.fromSpace);
      if (result.toSpace !== result.fromSpace) invalidateNoteLists(qc, result.toSpace);
      // Old and new parents' children / breadcrumbs of the subtree.
      qc.invalidateQueries({ queryKey: ["note"] });
    },
  });
}

export function useNoteVersions(id: string | undefined) {
  return useQuery({
    queryKey: keys.noteVersions(id ?? ""),
    queryFn: () => api.notes.versions(id!),
    enabled: !!id,
  });
}

/* ---------------- shares ---------------- */

export function useNoteShares(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.noteShares(id ?? ""),
    queryFn: () => api.notes.shares(id!),
    enabled: !!id && enabled,
  });
}

export function useCreateShare(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { oneTime?: boolean; expiresInHours?: number }) =>
      api.notes.createShare(noteId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.noteShares(noteId) }),
  });
}

export function useRevokeShare(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => api.shares.revoke(shareId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.noteShares(noteId) }),
  });
}

export function useSharedNote(token: string | undefined) {
  return useQuery({
    queryKey: keys.share(token ?? ""),
    queryFn: () => api.shares.read(token!),
    enabled: !!token,
    retry: false,
    staleTime: Infinity, // one-time links burn on read; never refetch
    gcTime: Infinity,
  });
}

/* ---------------- search / threads / recent ---------------- */

export function useSearch(q: string, space?: string, limit = 50) {
  return useQuery({
    queryKey: keys.search(q, space),
    queryFn: () => api.search(q, { space, limit }),
    enabled: q.trim().length > 0,
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });
}

export function useThreads(space?: string) {
  return useQuery<Thread[]>({
    queryKey: keys.threads(space),
    queryFn: () => api.threads.list({ space, limit: 100 }),
  });
}

export function useMoveThreadNote(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { noteId: string; toThreadId: string | null }) => api.threads.moveNote(threadId, v.noteId, v.toThreadId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["threads"] });
      qc.invalidateQueries({ queryKey: ["thread"] });
      qc.invalidateQueries({ queryKey: ["recent"] });
    },
  });
}

export function useThread(id: string | undefined) {
  return useQuery<Thread>({
    queryKey: keys.thread(id ?? ""),
    queryFn: () => api.threads.get(id!),
    enabled: !!id,
  });
}

export function useRecent(space?: string) {
  return useQuery({ queryKey: keys.recent(space), queryFn: () => api.recent(space) });
}

/* ---------------- tokens ---------------- */

export function useTokens() {
  return useQuery({ queryKey: keys.tokens, queryFn: api.tokens.list });
}

export function useCreateToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.tokens.create(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tokens }),
  });
}

export function useRevokeToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.tokens.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tokens }),
  });
}

/* ---------------- invites & users (admin) ---------------- */

export function useInvites(enabled = true) {
  return useQuery({ queryKey: keys.invites, queryFn: api.invites.list, enabled });
}

export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.invites.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invites }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.invites.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invites }),
  });
}

export function useInviteLookup(token: string | undefined) {
  return useQuery({
    queryKey: keys.invite(token ?? ""),
    queryFn: () => api.invites.lookup(token!),
    enabled: !!token,
    retry: false,
  });
}

export function useUsers(enabled = true) {
  return useQuery({ queryKey: keys.users, queryFn: api.users.list, enabled });
}

export function useSetUserDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      disabled ? api.users.disable(id) : api.users.enable(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.users }),
  });
}

/* ---------------- oauth consent ---------------- */

export function useOAuthRequest(id: string | undefined) {
  return useQuery({
    queryKey: keys.oauthRequest(id ?? ""),
    queryFn: () => api.oauth.request(id!),
    enabled: !!id,
    retry: false,
  });
}

export function useOAuthDecision() {
  return useMutation({
    mutationFn: ({ id, allow }: { id: string; allow: boolean }) => api.oauth.decide(id, allow),
  });
}
